import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, MailCheck, Link2, Unplug, Loader2, Send, ShieldCheck, Info, AlertTriangle, UserRound } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime, initials } from "@/lib/format";
import {
  PROFILE_PATH, useM365Status, connectOutlook, disconnectOutlook, sendTestMail,
  readM365Callback, stripM365Params, toastM365Callback,
} from "@/lib/m365";
import { cn } from "@/lib/utils";

const StatusPill = ({ connected, configured }) => (
  <span
    data-testid="m365-status-pill"
    data-state={!configured ? "unconfigured" : connected ? "connected" : "disconnected"}
    className={cn(
      "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap",
      connected ? "status-ok" : "border-dashed border-border text-muted-foreground"
    )}
  >
    {connected ? <MailCheck className="h-3.5 w-3.5" /> : <Mail className="h-3.5 w-3.5" />}
    {!configured ? "Ikkje sett opp" : connected ? "Kopla til" : "Ikkje kopla til"}
  </span>
);

const HowItWorks = () => (
  <ul className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
    {[
      ["Sendt frå deg", "Rapportar går frå di eiga e-postadresse – kunden svarar direkte til deg."],
      ["Kopi i Outlook", "Alt du sender ligg i «Sende element» i Outlook, som vanleg e-post."],
      ["Trygg lagring", "Portalen lagrar berre ein kryptert nøkkel – aldri passordet ditt."],
      ["Du bestemmer", "Du kan kople frå når som helst. Då blir nøkkelen sletta."],
    ].map(([t, d]) => (
      <li key={t} className="flex gap-2.5 rounded-lg border bg-card p-3">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#1F8A4C] dark:text-[#5BD08B]" />
        <span><span className="font-semibold text-foreground">{t}.</span> {d}</span>
      </li>
    ))}
  </ul>
);

export default function Profil() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const { status, loading, error, reload, configured, connected, connection } = useM365Status();
  const [busy, setBusy] = useState(""); // connect | test | disconnect
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  // Tilbake frå Microsoft: ?m365=connected|error&m365_msg=…
  useEffect(() => {
    const cb = readM365Callback(params);
    if (!cb) return;
    toastM365Callback(cb);
    setParams(stripM365Params(params), { replace: true });
  }, [params, setParams]);

  const connect = async () => {
    setBusy("connect");
    try {
      await connectOutlook(PROFILE_PATH);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikkje starte Outlook-koplinga.");
      setBusy("");
    }
  };

  const test = async () => {
    setBusy("test");
    try {
      const r = await sendTestMail();
      if (r.ok) toast.success(`Testmelding sendt til ${r.to}.`, { description: "Sjekk innboksen (og «Sende element») i Outlook." });
      else toast.error(r.detail || "Kunne ikkje sende testmelding.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikkje sende testmelding.");
      reload();
    } finally {
      setBusy("");
    }
  };

  const disconnect = async () => {
    setBusy("disconnect");
    try {
      await disconnectOutlook();
      toast.success("Outlook er kopla frå. Nøkkelen er sletta.");
      setConfirmDisconnect(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikkje kople frå.");
    } finally {
      setBusy("");
    }
  };

  const roleLabel = user?.title || (user?.role === "admin" ? "Administrator" : "Mekaniker");

  return (
    <div className="space-y-5" data-testid="profile-page">
      <PageHeader title="Min konto" description="Kontoopplysningar og Outlook-kopling for å sende rapportar direkte til kundar." />

      <div className="grid gap-5 lg:grid-cols-[320px_1fr]">
        {/* Brukar */}
        <Card className="shadow-sm lg:self-start" data-testid="profile-user-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#0B0D10] font-heading text-lg font-bold text-white dark:bg-white dark:text-[#0B0D10]">
                {initials(user?.name)}
              </span>
              <div className="min-w-0">
                <div className="truncate font-heading text-lg font-bold" data-testid="profile-user-name">{user?.name}</div>
                <div className="truncate text-sm text-muted-foreground" data-testid="profile-user-email">{user?.email}</div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="gap-1.5" data-testid="profile-user-role"><UserRound className="h-3.5 w-3.5" /> {roleLabel}</Badge>
              {user?.role === "admin" && <Badge className="bg-[#0B0D10] text-white hover:bg-[#0B0D10] dark:bg-white dark:text-[#0B0D10]">Admin</Badge>}
            </div>
            <p className="mt-4 text-xs text-muted-foreground">Namn, e-post og tittel blir endra av administrator under «Brukarar».</p>
          </CardContent>
        </Card>

        {/* Outlook / Microsoft 365 */}
        <Card className="shadow-sm" data-testid="m365-card">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <CardTitle className="flex items-center gap-2 font-heading text-base font-bold"><Mail className="h-4 w-4 text-primary" /> Outlook (Microsoft 365)</CardTitle>
                <CardDescription className="mt-1">Kople til éin gong – så kan du sende rapportar med PDF direkte frå rapportdetaljen.</CardDescription>
              </div>
              {(!loading || status) && <StatusPill connected={connected} configured={configured} />}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading && !status ? (
              <div className="space-y-3" data-testid="m365-loading">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-11 w-48" />
              </div>
            ) : error ? (
              <div className="status-overdue flex items-start gap-3 rounded-xl border p-4 text-sm" data-testid="m365-error">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="flex-1">
                  <div className="font-semibold">Kunne ikkje hente status</div>
                  <p className="mt-0.5 opacity-90">{error}</p>
                  <Button type="button" size="sm" variant="outline" className="mt-2" onClick={reload} data-testid="m365-retry">Prøv igjen</Button>
                </div>
              </div>
            ) : !configured ? (
              <div className="flex items-start gap-3 rounded-xl border border-primary/40 bg-primary/5 p-4 text-sm" data-testid="m365-not-configured">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#9a2f00] dark:text-primary" />
                <div>
                  <div className="font-semibold">Microsoft 365 er ikkje sett opp enno</div>
                  <p className="mt-0.5 text-muted-foreground">
                    Administrator må leggje inn app-registreringa frå Microsoft Entra (client ID, secret og redirect-URI) i serveroppsettet. Til då kan rapportar lastast ned som PDF og sendast frå e-postprogrammet ditt.
                  </p>
                </div>
              </div>
            ) : connected ? (
              <>
                <div className="flex flex-wrap items-center gap-4 rounded-xl border bg-card p-4" data-testid="m365-connection">
                  <span className="status-ok grid h-11 w-11 shrink-0 place-items-center rounded-xl border"><MailCheck className="h-5 w-5" /></span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold" data-testid="m365-account-email">{connection?.account_email || "Outlook-konto"}</div>
                    <div className="truncate text-xs text-muted-foreground" data-testid="m365-connection-meta">
                      {connection?.account_name ? `${connection.account_name} · ` : ""}
                      Kopla til {formatDateTime(connection?.connected_at)}
                      {connection?.last_used_at ? ` · sist brukt ${formatDateTime(connection.last_used_at)}` : " · ikkje brukt enno"}
                    </div>
                  </div>
                </div>
                {connection?.last_error && (
                  <div className="status-overdue flex items-start gap-3 rounded-xl border p-3 text-sm" data-testid="m365-last-error">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      <div className="font-semibold">Siste forsøk feila</div>
                      <p className="mt-0.5 opacity-90">{connection.last_error}</p>
                      <p className="mt-1 text-xs opacity-80">Kople frå og til på nytt om feilen held fram.</p>
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" onClick={test} disabled={!!busy} data-testid="m365-test-button">
                    {busy === "test" ? <Loader2 className="animate-spin" /> : <Send />} Send testmelding til meg
                  </Button>
                  <Button type="button" variant="ghost" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setConfirmDisconnect(true)} disabled={!!busy} data-testid="m365-disconnect-button">
                    <Unplug /> Kople frå
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="rounded-xl border bg-muted/40 p-4 text-sm text-muted-foreground" data-testid="m365-not-connected">
                  Du blir sendt til Microsoft for å logge inn med jobbkontoen din ({user?.email}). Etterpå kjem du rett tilbake hit.
                </div>
                <Button type="button" size="lg" className="h-12 w-full sm:w-auto" onClick={connect} disabled={!!busy} data-testid="m365-connect-button">
                  {busy === "connect" ? <Loader2 className="animate-spin" /> : <Link2 />} Kople til Outlook
                </Button>
              </>
            )}

            <HowItWorks />
          </CardContent>
        </Card>
      </div>


      <AlertDialog open={confirmDisconnect} onOpenChange={(o) => !busy && setConfirmDisconnect(o)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kople frå Outlook?</AlertDialogTitle>
            <AlertDialogDescription>
              Den lagra nøkkelen for {connection?.account_email || "kontoen"} blir sletta. Du kan ikkje sende rapportar frå portalen før du koplar til igjen. E-postar som alt er sende ligg framleis i Outlook.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!busy}>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); disconnect(); }} disabled={!!busy} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="m365-confirm-disconnect">
              {busy === "disconnect" ? <Loader2 className="animate-spin" /> : <Unplug />} Kople frå
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
